import { type Attributes, mergeAttributes, Node } from '@tiptap/core'
import { omit } from 'lodash-es'
import type { JsonObject } from '#src/json.ts'

/* Building blocks the node definitions in this directory share. */

export interface AttrDef {
  dataAttr?: string
  /* Parses from the element's text content rather than an attribute, and is
   * dropped from the rendered attribute set in favour of the node's child
   * text. */
  isDisplay?: boolean
  /* ProseMirror fills a missing attribute from the schema default while
   * parsing, so this decides what identical stored HTML materialises as. */
  default?: unknown
  /* Serialised to its data attribute as JSON. */
  json?: boolean
  /* Lives in the document but never crosses the HTML boundary: per-client,
   * per-session state that no other client could act on anyway. `rendered:
   * false` keeps it out of stored note HTML in both directions — never written
   * on a save, never parsed back on a read — while leaving it in the
   * ProseMirror doc (and so in JSON-stored content) for the session that set
   * it. `observation.autofocus` is the other attribute of this kind. */
  transient?: boolean
}

export interface ColumnHTMLAttributesOptions {
  HTMLAttributes: JsonObject
}

/* Every inline atom Seen defines has the same shape: a tag carrying
 * `data-type`, some data attributes, and its label as the element's text. One
 * factory rather than fifteen hand-expanded copies, so a node cannot acquire a
 * subtly different parse or render by being written out again. */
export function inlineAtomNode(
  name: string,
  tag: string,
  attrDefs: Record<string, AttrDef>
) {
  return Node.create({
    name,
    group: 'inline',
    inline: true,
    selectable: false,
    atom: true,
    addAttributes() {
      const result: Attributes = {}
      for (const [key, config] of Object.entries(attrDefs)) {
        const defaultValue = config.default ?? null
        const dataAttr = config.dataAttr ?? `data-${key}`
        if (config.transient) {
          result[key] = { default: defaultValue, rendered: false }
        } else if (config.isDisplay) {
          result[key] = {
            default: defaultValue,
            parseHTML: (element: HTMLElement) =>
              element.textContent ?? defaultValue,
          }
        } else if (config.json) {
          result[key] = {
            default: defaultValue,
            /* Falls back to the default on malformed JSON rather than
             * throwing: an uncaught throw here escapes into generateJSON and
             * fails the whole document over one attribute, and HTML reaching
             * this schema is authored elsewhere (the MCP write path included).
             * Matches the try/catch columnWidths already uses below. */
            parseHTML: (element: HTMLElement) => {
              const serialized = element.getAttribute(dataAttr)
              if (!serialized) {
                return defaultValue
              }
              try {
                return JSON.parse(serialized)
              } catch {
                return defaultValue
              }
            },
            renderHTML: (attributes: JsonObject) =>
              attributes[key]
                ? { [dataAttr]: JSON.stringify(attributes[key]) }
                : {},
          }
        } else {
          result[key] = {
            default: defaultValue,
            parseHTML: (element: HTMLElement) =>
              element.getAttribute(dataAttr) ?? defaultValue,
            renderHTML: (attributes: Record<string, string>) =>
              attributes[key] ? { [dataAttr]: attributes[key] } : {},
          }
        }
      }
      return result
    },
    parseHTML() {
      return [{ tag: `${tag}[data-type="${name}"]` }]
    },
    renderHTML({ HTMLAttributes }) {
      const display = HTMLAttributes.display
      return [
        tag,
        mergeAttributes({ 'data-type': name }, omit(HTMLAttributes, 'display')),
        ...(display ? [display] : []),
      ]
    },
  })
}

/* A block-level atom rendering an empty div tagged with its node name. The
 * `contenteditable="false"` is part of the serialised document: the browser
 * writes it into stored note HTML, so the server renders it too. */
export function blockAtomNode(name: string) {
  return Node.create({
    name,
    group: 'block',
    atom: true,
    parseHTML() {
      return [{ tag: `div[data-type="${name}"]` }]
    },
    renderHTML({ HTMLAttributes }) {
      return [
        'div',
        mergeAttributes(HTMLAttributes, {
          'data-type': name,
          contentEditable: 'false',
        }),
      ]
    },
  })
}
